import 'react-day-picker/dist/style.css';

import {
  ETooltipSize,
  ETooltipVariant,
  GeneralCaretLeftIcon,
  GeneralCaretRightIcon,
  Loader,
  Tooltip,
} from '@outdoorsyco/bonfire';
import cn from 'classnames';
/* eslint-disable import/no-duplicates */
import { isBefore, isSameDay, max, min } from 'date-fns';
import { enUS } from 'date-fns/locale';
/* eslint-enable import/no-duplicates */
import dayjs from 'dayjs';
import { lazy, Suspense, useCallback, useEffect, useMemo, useState } from 'react';
import { DayPickerProps } from 'react-day-picker';
import { FormattedMessage } from 'react-intl';

import { useLocaleSettings } from '@/hooks/useLocaleSettings';
import { getIsAndroidDevice, getIsIOSDevice } from '@/utility/navigator';

import css from './DatePicker.module.css';
import type { TMinimumStay } from './utils';
import {
  createGapBlockMatcher,
  getEffectiveMinimumStay,
  getMinimumStayNights,
  getModifiers,
  getStartRejection as computeStartRejection,
  isDayWithinMatcher,
  monthsForLocale,
  weekdaysForLocale,
} from './utils';

export type { TMinimumStay } from './utils';

const DayPicker = lazy(() =>
  import('react-day-picker').then(module => ({ default: module.DayPicker })),
);

export type TDatePickerProps = {
  dateFrom?: Date;
  dateTo?: Date;
  onChange?: (args: { from: Date | undefined; to: Date | undefined }) => void;
  maximumDays?: number;
  minimumStayRequirement?: TMinimumStay;
  disableBefore?: Date;
  // Pin the check-in date: clicks only move the check-out, never re-pick the start. Used once a
  // trip has started, when the start is in the past but the end can still be changed/extended.
  lockStartDate?: boolean;
  disabledDays?: DayPickerProps['disabled'];
  range?: boolean;
  allowSameDay?: boolean;
  isStacked?: boolean;
  size?: 'default' | 'small';
  // Hide the weekday header row (S M T W …).
  hideWeekdayHeader?: boolean;
  // Month heading label length. Defaults to 'long' ("January"); 'short' renders "Jan".
  monthLabelFormat?: 'short' | 'long';
  // External hover state props for shared hover across multiple DatePicker instances
  hoveredDay?: Date;
  onDayHover?: (day: Date | undefined) => void;
} & Omit<DayPickerProps, 'locale' | 'firstDayOfWeek' | 'disabled' | 'modifiers'>;

export const DatePicker = ({
  dateFrom,
  dateTo,
  onChange,
  maximumDays,
  minimumStayRequirement,
  disableBefore = new Date(),
  lockStartDate,
  disabledDays,
  range = true,
  allowSameDay,
  isStacked,
  size = 'default',
  hideWeekdayHeader,
  monthLabelFormat = 'long',
  hoveredDay: externalHoveredDay,
  onDayHover: externalOnDayHover,
  numberOfMonths = 1,
  ...rest
}: TDatePickerProps) => {
  const isMobile = getIsAndroidDevice() || getIsIOSDevice();

  const { locale, first_day_of_the_week: firstDayOfWeek } = useLocaleSettings();

  const parsedDisabledDays = useMemo(
    () => (disabledDays ? [...[disabledDays].flat()] : []),
    [disabledDays],
  );

  const gapBlockMatcher = useMemo(
    () => createGapBlockMatcher(dateFrom, dateTo, parsedDisabledDays),
    [dateFrom, dateTo, parsedDisabledDays],
  );

  const disabledDaysWithBeforeToday = useMemo(
    () => [
      { before: disableBefore },
      ...parsedDisabledDays,
      ...(gapBlockMatcher ? [gapBlockMatcher] : []),
    ],
    [parsedDisabledDays, disableBefore, gapBlockMatcher],
  );

  const [internalDayHover, setInternalDayHover] = useState<Date | undefined>();

  const dayHover = externalHoveredDay ?? internalDayHover;
  const setDayHover = externalOnDayHover ?? setInternalDayHover;

  const [rejectedStart, setRejectedStart] = useState<Date | undefined>();

  // Clear the rejected-start tooltip when the actual selection changes, or when the user moves to
  // another day. `dayHover` flips on hover and keyboard focus, and back to `undefined` on blur, so
  // moving away or leaving the calendar dismisses the tooltip. This is the desktop path; mobile
  // has no hover/focus, so it relies on the pointer-down handler below.
  useEffect(() => {
    setRejectedStart(undefined);
  }, [dateFrom, dateTo, dayHover]);

  // Touch has no hover/focus to dismiss the "can't start here" tooltip, so tapping off a day cell
  // closes it: while a tooltip is open, any pointer-down clears it, and a tap on a non-startable
  // day re-opens it via the day's own click handler (which fires after pointer-down). This also
  // keeps only one tooltip open at a time across the virtualized picker's per-month instances —
  // the tap closes whatever's open before the click opens the next.
  useEffect(() => {
    if (!isMobile || !rejectedStart) return undefined;

    const dismiss = () => setRejectedStart(undefined);
    document.addEventListener('pointerdown', dismiss);
    return () => document.removeEventListener('pointerdown', dismiss);
  }, [isMobile, rejectedStart]);

  // Minimum-stay geometry is resolved per-date (via getEffectiveMinimumStay), not once globally —
  // a seasonal rental can require different lengths depending on which start date is in play.
  const isDayUnavailable = useCallback(
    (day: Date) => parsedDisabledDays.some(matcher => isDayWithinMatcher(day, matcher)),
    [parsedDisabledDays],
  );

  const getStartRejection = useCallback(
    (day: Date) =>
      computeStartRejection(
        day,
        getMinimumStayNights(getEffectiveMinimumStay(minimumStayRequirement, day)),
        isDayUnavailable,
      ),
    [minimumStayRequirement, isDayUnavailable],
  );

  const isStartDisabled = useCallback(
    (day: Date) => getStartRejection(day) !== undefined,
    [getStartRejection],
  );

  // Once a start is selected, its own segment's minimum governs the rest of the geometry below
  // (the disabled-end-dates band and the highlighted minimum-stay window).
  const dateFromMinimumStayNights = useMemo(
    () => getMinimumStayNights(getEffectiveMinimumStay(minimumStayRequirement, dateFrom)),
    [minimumStayRequirement, dateFrom],
  );

  const modifiers = useMemo<DayPickerProps['modifiers']>(
    () =>
      getModifiers({
        range,
        dateFrom,
        dateTo,
        dayHover,
        maximumDays,
        minimumNights: dateFromMinimumStayNights,
        isStartDisabled,
      }),
    [range, dateFrom, dateTo, dayHover, maximumDays, dateFromMinimumStayNights, isStartDisabled],
  );

  // A start date whose minimum window is blocked is not selected; we remember it here and show
  // an always-open tooltip on that cell explaining why the selection didn't happen.
  const rejectedTooltip = useMemo(
    () => (rejectedStart ? getStartRejection(rejectedStart) : undefined),
    [rejectedStart, getStartRejection],
  );

  // Locale-aware day-of-month number. Memoized so a single formatter is reused across every cell
  // and hover-driven re-render (matters for locales that don't use Western-Arabic numerals).
  const dayNumberFormatter = useMemo(
    () => new Intl.DateTimeFormat(locale, { day: 'numeric' }),
    [locale],
  );

  // Memoized so its identity is stable across hover-driven re-renders. Passing a fresh
  // inline component to react-day-picker's `components` remounts every day cell on each
  // render, which tears down the tooltip before Popper can position it.
  const renderDayContent = useCallback<
    NonNullable<NonNullable<DayPickerProps['components']>['DayContent']>
  >(
    ({ date }) => {
      const dayLabel = <>{dayNumberFormatter.format(date)}</>;

      if (!rejectedTooltip || !rejectedStart || !isSameDay(date, rejectedStart)) {
        return dayLabel;
      }

      const rejectedStartMinimumStay = getEffectiveMinimumStay(
        minimumStayRequirement,
        rejectedStart,
      );

      // Render the day number normally and anchor the tooltip to a separate invisible,
      // cell-centered element. This keeps the number's own styling and avoids overriding
      // Bonfire's tooltip reference-wrapper styles.
      return (
        <>
          {dayLabel}
          <Tooltip
            controlled
            open
            placement="top"
            variant={ETooltipVariant.Default}
            size={ETooltipSize.Auto}
            // Invisible anchor centered on the cell. Bonfire's tooltip wrapper only accepts
            // a className (no style prop), and Popper anchors to it, so position it here.
            className="absolute pointer-events-none left-1/2 top-1/2 h-7 w-7 -translate-x-1/2 -translate-y-1/2"
            content={
              rejectedTooltip === 'checkout' ? (
                <FormattedMessage
                  id="Mq7njq"
                  defaultMessage="Checkout only"
                  description="Availability calendar > tooltip on a selected start date whose next day is unavailable, so it can only be used as a checkout date"
                />
              ) : (
                <FormattedMessage
                  id="d+TiZv"
                  defaultMessage="{priceType, select, day {{count, plural, one {# day} other {# days}}} other {{count, plural, one {# night} other {# nights}}}} minimum"
                  description="Availability calendar > tooltip when the minimum trip length cannot be met from the selected start date"
                  values={{
                    count: rejectedStartMinimumStay?.duration ?? 0,
                    priceType: rejectedStartMinimumStay?.use_day_pricing ? 'day' : 'night',
                  }}
                />
              )
            }>
            <span />
          </Tooltip>
        </>
      );
    },
    [rejectedTooltip, rejectedStart, dayNumberFormatter, minimumStayRequirement],
  );

  const renderIconRight = useCallback<
    NonNullable<NonNullable<DayPickerProps['components']>['IconRight']>
  >(() => <GeneralCaretRightIcon className="w-5 h-5" />, []);

  const renderIconLeft = useCallback<
    NonNullable<NonNullable<DayPickerProps['components']>['IconLeft']>
  >(() => <GeneralCaretLeftIcon className="w-5 h-5" />, []);

  // Locale label sets only depend on the locale, so memoize them past hover-driven re-renders.
  const { months, weekdaysShort, weekdaysLong } = useMemo(
    () => ({
      months: monthsForLocale(locale, monthLabelFormat),
      weekdaysShort: weekdaysForLocale(locale, 'narrow'),
      weekdaysLong: weekdaysForLocale(locale, 'long'),
    }),
    [locale, monthLabelFormat],
  );

  const handleDayClick = useCallback(
    (day: Date) => {
      // Start is pinned: keep the existing `from` and only move the check-out to the clicked day.
      if (lockStartDate) {
        onChange?.({ from: dateFrom, to: day });
        return;
      }

      const isTheSameDay = dateFrom && isSameDay(day, dateFrom);

      // Single-date mode: clicking a new day selects it, clicking the selected day again clears
      // it (toggle). There is no end date, so `to` stays undefined.
      if (!range) {
        onChange?.({ from: isTheSameDay ? undefined : day, to: undefined });
        return;
      }

      const isSelectingFirstDay = !dateFrom || (dateFrom && dateTo);

      if (isTheSameDay && !allowSameDay) {
        onChange?.({ from: day, to: undefined });
        return;
      }

      if (isSelectingFirstDay) {
        // If the minimum stay can't start on this date, don't select it — keep the current
        // selection and surface the reason via an always-open tooltip on the clicked cell.
        if (getStartRejection(day)) {
          setRejectedStart(day);
          return;
        }

        setRejectedStart(undefined);
        onChange?.({ from: day, to: undefined });
        return;
      }

      const isBeforeFirstDay = dateFrom && day && isBefore(day, dateFrom);

      if (isBeforeFirstDay) {
        onChange?.({
          from:
            maximumDays && maximumDays > 0
              ? max([
                  day,
                  dayjs(dateFrom)
                    .subtract(maximumDays - 1, 'day')
                    .toDate(),
                ])
              : day,
          to: dateFrom,
        });
        return;
      }

      onChange?.({
        from: dateFrom,
        to:
          maximumDays && maximumDays > 0
            ? min([
                day,
                dayjs(dateFrom)
                  .add(maximumDays - 1, 'day')
                  .toDate(),
              ])
            : day,
      });
    },
    [
      dateFrom,
      dateTo,
      range,
      lockStartDate,
      onChange,
      allowSameDay,
      getStartRejection,
      maximumDays,
      setRejectedStart,
    ],
  );

  // react-day-picker renders disabled days as native `<button disabled>`, which never fire
  // keydown, so this only runs for selectable days — no disabled check needed.
  const handleKeyDown = useCallback<NonNullable<DayPickerProps['onDayKeyDown']>>(
    (day, _, e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        handleDayClick(day);
      }
    },
    [handleDayClick],
  );

  const handleDayHover = useCallback<NonNullable<DayPickerProps['onDayMouseEnter']>>(
    day => setDayHover(day),
    [setDayHover],
  );

  const handleDayFocus = useCallback<NonNullable<DayPickerProps['onDayFocus']>>(
    day => setDayHover(day),
    [setDayHover],
  );

  const handleDayBlur = useCallback<NonNullable<DayPickerProps['onDayBlur']>>(
    () => setDayHover(undefined),
    [setDayHover],
  );

  const selectedDays = dateFrom && [dateFrom, { from: dateFrom, to: dateTo as Date }];

  return (
    <div
      className={cn(css.root, {
        [css.small as string]: size === 'small',
        [css.stacked as string]: isStacked,
      })}
      data-testid="date-calendar">
      <Suspense
        fallback={
          <div className="relative h-100">
            <Loader className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2" />
          </div>
        }>
        <DayPicker
          // Override en-us locale to use our own weekdays and months.
          // In the future maybe we would like to use raw locale from date-fns.
          locale={{
            ...enUS,
            localize: enUS.localize && {
              ...enUS.localize,
              day: (dayIndex: number, options: { width: 'short' | 'wide' }) => {
                return options.width === 'short' ? weekdaysShort[dayIndex] : weekdaysLong[dayIndex];
              },
              month: (monthIndex: number) => months[monthIndex],
            },
            options: {
              weekStartsOn: firstDayOfWeek as 0 | 1,
            },
          }}
          disabled={disabledDaysWithBeforeToday}
          selected={selectedDays}
          modifiers={modifiers}
          modifiersClassNames={{
            hover: isMobile ? '' : (css.hover as string),
            single: css.single as string,
            start: css.start as string,
            end: css.end as string,
            interval: css.interval as string,
            intervalFirst: css.intervalFirst as string,
            intervalLast: css.intervalLast as string,
            minimum: css.minimum as string,
            minimumFirst: css.minimumFirst as string,
            minimumLast: css.minimumLast as string,
            minimumStayDisabled: css.minimumStayDisabled as string,
            minimumStartDisabled: css.minimumStartDisabled as string,
          }}
          numberOfMonths={numberOfMonths}
          onDayClick={handleDayClick}
          onDayKeyDown={isMobile ? undefined : handleKeyDown}
          onDayMouseEnter={isMobile ? undefined : handleDayHover}
          onDayFocus={isMobile ? undefined : handleDayFocus}
          onDayBlur={isMobile ? undefined : handleDayBlur}
          components={{
            DayContent: renderDayContent,
            IconRight: renderIconRight,
            IconLeft: renderIconLeft,
          }}
          fixedWeeks={true}
          hideHead={hideWeekdayHeader}
          // We don't do a good job on differentiating range and single date pickers from types.
          // Just cast as an object to avoid typescript errors for now.
          {...(rest as object)}
        />
      </Suspense>
    </div>
  );
};
